import { useEffect, useState, useCallback } from "react";
import { Check, ThermometerSun, Sparkles, PackageCheck, Apple, ShieldAlert, GraduationCap, Save, Plus, Trash2, AlertTriangle, Mic } from "lucide-react";
import { api, todayISO } from "../lib/api";
import { toast } from "sonner";
import VoiceHaccp from "../components/VoiceHaccp";

const CATEGORY_ICONS = {
    Temperature: ThermometerSun,
    Pulizia: Sparkles,
    Ricevimento: PackageCheck,
    Scadenze: Apple,
    "Igiene personale": ShieldAlert,
    Formazione: GraduationCap,
};

function TaskCard({ task, onToggle, onValueChange, onNoteChange, onCarryYesterday }) {
    const Icon = CATEGORY_ICONS[task.categoria] || Check;
    const done = task.completato;
    const numericVal = task.valore !== "" && task.valore != null ? parseFloat(String(task.valore).replace(",", ".")) : null;
    const hasRange = task.tipo_valore === "celsius" && task.min_c != null && task.max_c != null;
    const buffer = task.buffer_c ?? 0;
    const outOfRange =
        hasRange && numericVal != null && !Number.isNaN(numericVal) && (numericVal < task.min_c || numericVal > task.max_c);
    const warning =
        hasRange &&
        numericVal != null &&
        !Number.isNaN(numericVal) &&
        !outOfRange &&
        buffer > 0 &&
        (numericVal < task.min_c + buffer || numericVal > task.max_c - buffer);

    return (
        <div
            data-testid={`task-${task.key}`}
            className={`rounded-2xl border p-5 transition-all ${
                outOfRange
                    ? "bg-red-50 border-red-300 shadow-[0_2px_8px_rgba(192,57,43,0.10)]"
                    : warning
                    ? "bg-amber-50 border-amber-300"
                    : done
                    ? "bg-forest-50 border-[color:var(--moss)]/40 shadow-[0_2px_8px_rgba(74,124,89,0.08)]"
                    : "bg-white border-soft shadow-[0_2px_8px_rgba(44,76,59,0.04)]"
            }`}
        >
            <div className="flex items-start gap-4">
                <button
                    type="button"
                    onClick={() => onToggle(!done)}
                    data-testid={`task-toggle-${task.key}`}
                    className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center border-2 transition-all ${
                        done
                            ? "bg-forest border-forest text-white"
                            : "bg-white border-stone-200 text-stone-400 hover:border-forest"
                    }`}
                    aria-label={done ? "Segna come non completato" : "Segna come completato"}
                >
                    {done ? <Check size={20} strokeWidth={3} /> : <Icon size={18} />}
                </button>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-forest-500">
                        {task.categoria}
                    </p>
                    <h3 className="font-display font-semibold text-forest-dark mt-0.5">{task.titolo}</h3>
                    {task.soglia && (
                        <p className="text-xs text-stone-500 mt-0.5">
                            Soglia: {task.soglia}
                            {buffer > 0 && <span className="text-amber-700"> · warning a ±{buffer}°C dal limite</span>}
                        </p>
                    )}

                    {task.tipo_valore === "celsius" && (
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <input
                                type="number"
                                step="0.1"
                                placeholder="°C"
                                value={task.valore ?? ""}
                                onChange={(e) => onValueChange(e.target.value)}
                                data-testid={`task-temp-${task.key}`}
                                className={`input-soft max-w-[110px] ${
                                    outOfRange ? "border-red-400 focus:ring-red-300" : warning ? "border-amber-400" : ""
                                }`}
                            />
                            <span className="text-sm text-stone-500">gradi</span>
                            {task.auto_filled && (
                                <span
                                    className="chip bg-blue-100 text-blue-800 border border-blue-200"
                                    data-testid={`task-autofilled-${task.key}`}
                                    title="Lettura dimenticata: inserita automaticamente la media delle ultime letture"
                                >
                                    ⟳ Media automatica
                                </span>
                            )}
                            {(task.valore === null || task.valore === "" || task.valore === undefined) && task.suggested_value != null && (
                                <button
                                    type="button"
                                    onClick={() => onCarryYesterday?.(task.suggested_value, task.suggested_from)}
                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200 hover:bg-blue-200 active:scale-95"
                                    data-testid={`task-carry-${task.key}`}
                                    title="Riporta il valore del giorno precedente e conferma"
                                >
                                    ↩ Riporta ieri ({task.suggested_value}°C)
                                </button>
                            )}
                            {outOfRange && (
                                <span
                                    className="chip bg-red-100 text-red-700 border border-red-200"
                                    data-testid={`task-warn-${task.key}`}
                                >
                                    <AlertTriangle size={12} /> Fuori soglia!
                                </span>
                            )}
                            {warning && !outOfRange && (
                                <span
                                    className="chip bg-amber-100 text-amber-800 border border-amber-200"
                                    data-testid={`task-warning-${task.key}`}
                                >
                                    <AlertTriangle size={12} /> Vicino al limite
                                </span>
                            )}
                        </div>
                    )}
                    {outOfRange && (
                        <p
                            className="text-xs text-red-700 mt-2 bg-red-100/50 rounded-lg px-2 py-1"
                            data-testid={`task-warn-text-${task.key}`}
                        >
                            La temperatura {numericVal}°C è fuori dal range consentito ({task.min_c}°C — {task.max_c}°C).
                            Verifica il frigorifero e registra l'azione correttiva nelle non conformità.
                        </p>
                    )}
                    {warning && !outOfRange && (
                        <p
                            className="text-xs text-amber-800 mt-2 bg-amber-100/50 rounded-lg px-2 py-1"
                            data-testid={`task-warning-text-${task.key}`}
                        >
                            Attenzione: {numericVal}°C è vicino al limite ({task.min_c}°C — {task.max_c}°C, margine {buffer}°C).
                            Controlla il frigorifero prima che diventi una non conformità.
                        </p>
                    )}

                    <input
                        type="text"
                        placeholder="Note (opzionale)"
                        value={task.note || ""}
                        onChange={(e) => onNoteChange(e.target.value)}
                        data-testid={`task-note-${task.key}`}
                        className="input-soft mt-3 text-sm"
                    />
                </div>
            </div>
        </div>
    );
}

export default function Haccp() {
    const [day, setDay] = useState(todayISO());
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState("");

    const [ncOpen, setNcOpen] = useState(false);
    const [ncTitle, setNcTitle] = useState("");
    const [ncNote, setNcNote] = useState("");
    const [records, setRecords] = useState([]);
    const [voiceOpen, setVoiceOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [tplRes, ncRes] = await Promise.all([
                api.get(`/haccp/checklist/${day}`),
                api.get(`/haccp/records`, { params: { tipo: "non_conformita" } }),
            ]);
            setTasks(tplRes.data);
            setRecords(ncRes.data);
        } catch {
            toast.error("Errore caricamento checklist");
        } finally {
            setLoading(false);
        }
    }, [day]);

    useEffect(() => {
        load();
    }, [load]);

    async function persist(task, patch) {
        const next = { ...task, ...patch };
        setTasks((cur) => cur.map((t) => (t.key === task.key ? next : t)));
        setSavingKey(task.key);
        try {
            await api.post("/haccp/checklist", {
                data: day,
                task_key: task.key,
                completato: next.completato,
                note: next.note || "",
                valore: next.valore || null,
            });
            // Out-of-range alert on temperature input change
            if (patch.valore !== undefined && next.tipo_valore === "celsius" && next.min_c != null) {
                const v = parseFloat(String(next.valore).replace(",", "."));
                if (!Number.isNaN(v) && (v < next.min_c || v > next.max_c)) {
                    toast.warning(`Temperatura fuori soglia su "${next.frigo_nome || next.titolo}"`, {
                        description: `${v}°C non rientra nel range ${next.min_c}°C — ${next.max_c}°C. Registra l'azione correttiva.`,
                        action: {
                            label: "Apri non conformità",
                            onClick: () => {
                                setNcTitle(`Temperatura fuori soglia: ${next.frigo_nome || next.titolo} (${v}°C)`);
                                setNcNote(`Range richiesto ${next.min_c}°C — ${next.max_c}°C. Azione correttiva: `);
                                setNcOpen(true);
                                setTimeout(() => {
                                    document.querySelector('[data-testid="nc-note"]')?.focus();
                                }, 100);
                            },
                        },
                        duration: 8000,
                    });
                }
            }
        } catch {
            toast.error("Errore salvataggio");
            setTasks((cur) => cur.map((t) => (t.key === task.key ? task : t)));
        } finally {
            setSavingKey("");
        }
    }

    async function addNonConformity() {
        if (!ncTitle.trim()) {
            toast.error("Inserisci un titolo");
            return;
        }
        try {
            await api.post("/haccp/records", {
                data: day,
                tipo: "non_conformita",
                titolo: ncTitle,
                note: ncNote,
            });
            toast.success("Non conformità registrata");
            setNcTitle("");
            setNcNote("");
            setNcOpen(false);
            load();
        } catch {
            toast.error("Errore salvataggio");
        }
    }

    async function delRecord(id) {
        await api.delete(`/haccp/records/${id}`);
        toast.success("Eliminata");
        load();
    }

    const byCat = tasks.reduce((acc, t) => {
        acc[t.categoria] = acc[t.categoria] || [];
        acc[t.categoria].push(t);
        return acc;
    }, {});

    const completed = tasks.filter((t) => t.completato).length;
    const pct = tasks.length ? Math.round((completed / tasks.length) * 100) : 0;

    return (
        <div className="space-y-6 pb-6" data-testid="haccp-page">
            <VoiceHaccp
                open={voiceOpen}
                onClose={() => setVoiceOpen(false)}
                onApplied={load}
                dataGiorno={day}
            />

            {/* Voice CTA — big and obvious */}
            <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="w-full flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-forest-700 to-forest-900 text-white shadow-lg hover:shadow-xl transition-all active:scale-[0.99]"
                data-testid="haccp-voice-open"
            >
                <div className="w-14 h-14 rounded-2xl bg-white/20 grid place-items-center shrink-0">
                    <Mic size={28} />
                </div>
                <div className="text-left flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-widest opacity-80">Nuovo · AI</p>
                    <p className="font-display text-lg font-semibold leading-snug">Compila il registro con la voce</p>
                    <p className="text-xs opacity-90 mt-0.5">Parla e l'app riempie automaticamente i campi</p>
                </div>
            </button>

            <div className="card-soft p-5 flex flex-wrap items-center gap-4 justify-between">
                <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-forest-500">
                        Giornata di lavoro
                    </p>
                    <input
                        type="date"
                        value={day}
                        onChange={(e) => setDay(e.target.value)}
                        data-testid="haccp-date-picker"
                        className="input-soft mt-2 max-w-[200px]"
                    />
                </div>
                <div className="text-right">
                    <p className="text-xs text-stone-500 uppercase tracking-wider">Completamento</p>
                    <p className="font-display text-3xl font-semibold text-forest">{pct}%</p>
                    <p className="text-xs text-stone-500">{completed}/{tasks.length} attività</p>
                </div>
            </div>

            {loading ? (
                <div className="grid sm:grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="card-soft p-5 h-32 animate-pulse" />
                    ))}
                </div>
            ) : (
                Object.entries(byCat).map(([cat, items]) => (
                    <section key={cat}>
                        <h2 className="font-display text-lg font-semibold text-forest-dark mb-3">{cat}</h2>
                        <div className="grid sm:grid-cols-2 gap-4 stagger">
                            {items.map((t) => (
                                <TaskCard
                                    key={t.key}
                                    task={t}
                                    onToggle={(v) => persist(t, { completato: v })}
                                    onValueChange={(v) => persist(t, { valore: v })}
                                    onNoteChange={(v) => persist(t, { note: v })}
                                    onCarryYesterday={(v, from) => {
                                        const extra = from ? ` (${from})` : "";
                                        persist(t, {
                                            valore: String(v),
                                            completato: true,
                                            note: (t.note ? t.note + " · " : "") + "Riportato dal giorno precedente" + extra,
                                        });
                                    }}
                                />
                            ))}
                        </div>
                    </section>
                ))
            )}

            {savingKey && (
                <div className="fixed bottom-24 lg:bottom-6 right-6 bg-forest text-white text-xs px-3 py-2 rounded-full shadow-lg flex items-center gap-2 z-40">
                    <Save size={14} /> Salvataggio...
                </div>
            )}

            <section className="card-soft p-6" data-testid="non-conformity-section">
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <div>
                        <h2 className="font-display text-xl font-semibold text-forest-dark">
                            Non conformità e azioni correttive
                        </h2>
                        <p className="text-sm text-stone-500 mt-0.5">
                            Registra anomalie, scarti, problemi e relativa azione adottata.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setNcOpen((o) => !o)}
                        className="btn-accent text-sm inline-flex items-center gap-2"
                        data-testid="nc-add-toggle"
                    >
                        <Plus size={16} /> Aggiungi
                    </button>
                </div>

                {ncOpen && (
                    <div className="bg-bone-2 rounded-xl p-4 mb-4 space-y-3" data-testid="nc-form">
                        <input
                            className="input-soft"
                            placeholder="Titolo (es. Temperatura cella fuori range)"
                            value={ncTitle}
                            onChange={(e) => setNcTitle(e.target.value)}
                            data-testid="nc-title"
                        />
                        <textarea
                            className="input-soft min-h-[80px]"
                            placeholder="Descrizione e azione correttiva adottata"
                            value={ncNote}
                            onChange={(e) => setNcNote(e.target.value)}
                            data-testid="nc-note"
                        />
                        <button onClick={addNonConformity} className="btn-primary text-sm" data-testid="nc-save">
                            Registra
                        </button>
                    </div>
                )}

                {!records.length ? (
                    <p className="text-sm text-stone-500 text-center py-6">
                        Nessuna non conformità registrata. 👏
                    </p>
                ) : (
                    <ul className="divide-y divide-[color:var(--border-soft)]">
                        {records.map((r) => (
                            <li
                                key={r.id}
                                className="py-3 flex items-start justify-between gap-3"
                                data-testid={`nc-row-${r.id}`}
                            >
                                <div className="min-w-0">
                                    <p className="text-xs font-semibold text-terracotta-dark uppercase tracking-wider">
                                        {r.data}
                                    </p>
                                    <p className="font-semibold text-forest-dark">{r.titolo}</p>
                                    {r.note && <p className="text-sm text-stone-500 mt-0.5">{r.note}</p>}
                                </div>
                                <button
                                    onClick={() => delRecord(r.id)}
                                    className="text-stone-400 hover:text-red-600 p-2"
                                    data-testid={`nc-del-${r.id}`}
                                    aria-label="Elimina"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
